import React, { useMemo, useCallback, Fragment } from 'react';

import { useLocalizedStrings } from 'app/localization/Context';

import { ReactComponent as IconDisconnect } from 'images/general/disconnect.svg';
import { ReactComponent as IconThermostatWheel } from 'images/device/thermostat/thermostat-wheel.svg';
import { ReactComponent as IconHeating } from 'images/device/thermostat/heating-icon-in-wheel.svg';
import { ReactComponent as IconCooling } from 'images/device/thermostat/device_detail/mode_cooling.svg';

import "./ThermostatWheel.scss";

function polarToCartesian(centerX, centerY, radius, angleInAngles) {
    var angleInRadians = (angleInAngles - 90) * Math.PI / 180.0;

    return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
    };
}

function describeArc(centerX, centerY, radius, startAngle, endAngle) {
    var start = polarToCartesian(centerX, centerY, radius, endAngle);
    var end = polarToCartesian(centerX, centerY, radius, startAngle);

    var largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    var d = [
        "M", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");

    return d;
}

const minAngle = -75;
const maxAngle = 75;

function angleToTemperature(minTemperature, maxTemperature, angle) {
    return (maxTemperature - minTemperature) * ((angle - minAngle) / (maxAngle - minAngle)) + minTemperature;
}

function temperatureToAngle(minTemperature, maxTemperature, temperature) {
    return (maxAngle - minAngle) * ((temperature - minTemperature) / (maxTemperature - minTemperature)) + minAngle;
}

function markupFloat(f, style) {
    if (f !== undefined) {
        const intPart = (f).toFixed(1).split(".")[0]
        const realPart = (f).toFixed(1).split(".")[1]
        return <div className="content" style={style}>
            <div className="int-part">{intPart} </div>
            <div className="float-part">.{realPart}</div>
        </div>;
    } else
        return "—";
}

export default function ThermostatWheel({
    componentName,
    children, // whatever inside the main circle

    minTargetTemperature, maxTargetTemperature,
    currentTemperature,

    targetTemperature, setTargetTemperature, setTempTargetTemperature,
    shouldRoundHalfTargetTemperature,

    canEdit, isTransparent, showTemperatures, showSetpoint, isDisconnected,

    forLightBackground, canShowArc
}) {
    const _maxTargetTemperature = maxTargetTemperature === undefined ? 35 : maxTargetTemperature;
    const _minTargetTemperature = minTargetTemperature === undefined ? 10 : minTargetTemperature;

    const minTemperature = _minTargetTemperature;
    const maxTemperature = _maxTargetTemperature;

    const s = useLocalizedStrings();

    const currentTemperatureAngle = useMemo(() => {
        return temperatureToAngle(minTemperature, maxTemperature,
            Math.min(Math.max(currentTemperature, minTemperature), maxTemperature))
    }, [currentTemperature, minTemperature, maxTemperature]);

    const targetTemperatureAngle = useMemo(() => {
        return temperatureToAngle(minTemperature, maxTemperature,
            Math.min(Math.max(targetTemperature, minTemperature), maxTemperature))
    }, [targetTemperature, minTemperature, maxTemperature]);

    const setTempTargetTemperatureAngle = useCallback((v) => {
        let t = angleToTemperature(minTemperature, maxTemperature, v);
        t = Math.min(_maxTargetTemperature, t);
        t = Math.max(_minTargetTemperature, t);
        setTempTargetTemperature(t);
    }, [setTempTargetTemperature, minTemperature, maxTemperature, _minTargetTemperature, _maxTargetTemperature]);

    const setTargetTemperatureAngle = useCallback((v) => {
        let t = angleToTemperature(minTemperature, maxTemperature, v);
        t = Math.min(_maxTargetTemperature, t);
        t = Math.max(_minTargetTemperature, t);
        if (shouldRoundHalfTargetTemperature)
            t = Math.round(t * 2) / 2;
        setTargetTemperature(t);
    }, [setTargetTemperature,
        shouldRoundHalfTargetTemperature,
        minTemperature, maxTemperature,
        _minTargetTemperature, _maxTargetTemperature]);

    // const radius = 252 / 2;
    // c2 = a2 + b2 - 2ab cos(C)
    // const temperatureCircleRadius = 12;
    // const radiansDiffForPath = Math.acos((2*radius*radius - temperatureCircleRadius*temperatureCircleRadius)/(2*radius*radius));
    // const angleDiffForPath = (180/Math.PI) * radiansDiffForPath;
    // const angleDiffForPath = 3;

    const isTemperatureDirectionForward = currentTemperatureAngle < targetTemperatureAngle;

    const temperaturePath = useMemo(() => {
        const isForward = isTemperatureDirectionForward; // is heating
        const markerColor = isForward ? "#ff4700" : "#23d4ff";
        const startAngle = isForward
            ? currentTemperatureAngle + 5
            : targetTemperatureAngle + 12;
        const endAngle = isForward
            ? targetTemperatureAngle - 12
            : currentTemperatureAngle - 5;

        if (endAngle < startAngle) {
            return null;
        }

        const markerID = componentName + "_arrowhead_" + (isForward ? "start" : "end");

        return (
            <svg className="temperature-path" style={{ marginTop: -8 }} width="102" height="102" viewBox="0 -2 102 100">
                <defs>
                    <linearGradient id={componentName + "_gradient_arc"}>
                        <stop offset="0" stopColor={isForward ? "#FFFFFF" : "#23d4ff"}>
                            <animate attributeName="stop-color"
                                values={isForward ? "#FFFFFF;#FFFFFF;#ff4700;#FFFFFF" : "#23d4ff;#23d4ff;#23d4ff;#23d4ff"}
                                dur="1.5s" repeatCount="indefinite" />
                        </stop>
                        <stop>
                            <animate attributeName="offset"
                                values={isForward ? "0;1;1;1" : "1;0;0;0"}
                                dur="1.5s" repeatCount="indefinite" />
                            <animate attributeName="stop-color"
                                values={isForward ? "#FFFFFF;#FFFFFF;#ff4700;#ff4700" : "#FFFFFF;#FFFFFF;#23d4ff;#23d4ff"}
                                dur="1.5s" repeatCount="indefinite" />
                        </stop>
                        <stop offset="100%" stopColor={isForward ? "#ff4700" : "#FFFFFF"}>
                            <animate attributeName="stop-color"
                                values={isForward ? "#ff4700;#ff4700;#ff4700;#ff4700" : "#FFFFFF;#FFFFFF;#23d4ff;#FFFFFF"}
                                dur="1.5s" repeatCount="indefinite" />
                        </stop>
                    </linearGradient>
                    <marker
                        id={markerID}
                        markerWidth="3"
                        markerHeight="3"
                        refX={isForward ? "2" : "1.5"}
                        refY="1.5"
                        orient="auto"
                    >
                        <polygon
                            points={isForward ? "3 0, 0 1.5, 3 3" : "0 0, 3 1.5, 0 3"}
                            fill={markerColor}
                        />
                    </marker>
                </defs>
                <path
                    d={describeArc(51, 51, 50, startAngle, endAngle)}
                    stroke={"url(#" + componentName + "_gradient_arc)"}
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    markerStart={isForward ? `url(#${markerID})` : undefined}
                    markerEnd={isForward ? undefined : `url(#${markerID})`}
                />
            </svg>
        );
    }, [componentName, targetTemperatureAngle, currentTemperatureAngle, isTemperatureDirectionForward]);


    const targetTemperatureCircleColor = useMemo(() => {
        if (isTemperatureDirectionForward) {
            return "#ff4700"
        } else {
            return "#23d4ff"
        }
    }, [isTemperatureDirectionForward]);

    const calcTargetTemperatureAngle = useCallback((e) => {
        const rect = e.target.getBoundingClientRect();
        const x = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const y = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
        // const x = e.clientX;
        // const y = e.clientY;

        const dx = x - rect.left;
        const dy = y - rect.top;

        if (dy / rect.height > 0.55) {
            return undefined;
        }

        let alpha = (180 / Math.PI) * Math.atan((dx - rect.width / 2) / (rect.height / 2 - dy));
        if ((rect.height / 2 - dy) < 0) {
            if (alpha > 0) {
                alpha = -90;
            } else {
                alpha = +90;
            }
        }
        alpha = Math.min(maxAngle, Math.max(minAngle, alpha));

        return alpha;
    }, []);

    const onTouch = useCallback((e) => {
        e.preventDefault();
        const alpha = calcTargetTemperatureAngle(e);
        if (alpha !== undefined)
            setTempTargetTemperatureAngle(alpha);         // to show on the circle
    }, [setTempTargetTemperatureAngle, calcTargetTemperatureAngle]);

    const onClick = useCallback((e) => { // onClick OR onTouchEnd
        e.preventDefault();
        const alpha = calcTargetTemperatureAngle(e);
        if (alpha !== undefined)
            setTargetTemperatureAngle(alpha);
    }, [setTargetTemperatureAngle, calcTargetTemperatureAngle]);

    const wheelIconTransform = `translate(-50%, -50%) rotate(${-45 + targetTemperatureAngle}deg)`;

    const showCurrentTemperature = !showSetpoint || ((currentTemperature !== undefined) && (Math.abs(currentTemperature - targetTemperature) > 0.5))
    const showArc = showCurrentTemperature && (canShowArc ? canShowArc(currentTemperature, targetTemperature) : false);

    return (
        <div className="thermostat-wheel" style={isTransparent ? { opacity: 0.35 } : {}}>
            <div className={forLightBackground ? "semicircular-bg light" : "semicircular-bg"} />
            <IconHeating className="heating-icon" />
            <IconCooling className="cooling-icon" />

            {showTemperatures ? (
                showSetpoint ? <Fragment>
                    <IconThermostatWheel className="main-circle" style={{ transform: wheelIconTransform }} />
                    {showArc ? temperaturePath : null}

                    {showCurrentTemperature ? <div className={"temperature-dot-container " + (forLightBackground ? "black" : "")}
                        style={{ transform: `rotate(${currentTemperatureAngle}deg)` }}>
                        <div className="current-temperature-dot"></div>
                        {markupFloat(currentTemperature, {
                            transform: `rotate(${-currentTemperatureAngle}deg)`,
                        })}
                    </div> : null}

                    {/* test dot with target */}
                    {/* <div className="temperature-dot-container"
                        style={{ transform: `rotate(${targetTemperatureAngle}deg)` }}>
                        <div className="current-temperature-dot" style={{ backgroundColor: targetTemperatureCircleColor }}></div>
                        {markupFloat(targetTemperature, {
                            transform: `rotate(${-targetTemperatureAngle}deg)`,
                        })}
                    </div> */}

                    <div className="temperature-circle-container"
                        style={{ transform: `rotate(${targetTemperatureAngle}deg)` }}>
                        <div className="target-temperature-circle"
                            style={{
                                transform: `rotate(${-targetTemperatureAngle}deg)`,
                                backgroundColor: targetTemperatureCircleColor
                            }}>
                            {markupFloat(targetTemperature)}
                        </div>
                    </div>
                </Fragment> :
                    <Fragment>
                        <div className="main-circle-simplified" />
                        {currentTemperature !== undefined ? <div className={"temperature-dot-container " + (forLightBackground ? "black" : "")}
                            style={{ transform: `rotate(${currentTemperatureAngle}deg)` }}>
                            <div className="current-temperature-dot"></div>
                            {markupFloat(currentTemperature, {
                                transform: `rotate(${-currentTemperatureAngle}deg)`,
                            })}
                        </div> : null}

                    </Fragment>) :
                <div className="main-circle-simplified" />}

            {canEdit ?
                <div className="touch-field"
                    onClick={onClick}
                    onTouchStart={onTouch} onTouchMove={onTouch}
                    onTouchEnd={onClick} />
                : null}

            <div className="thermostat-wheel-data-container-1">
                <div className="thermostat-wheel-data-container-2">
                    {children}
                </div>
            </div>

            {isDisconnected ?
                <div className="dc-panel">
                    <div className="dc-group">
                        <IconDisconnect className="icon" />
                        <div className="label">{s.deviceDC}</div>
                    </div>
                </div> : null}
        </div>
    );
}